import { useDispatch, useSelector } from 'react-redux';
import { getSignupDataValue, getSignupDataGtkmData, getSignupDataSkipped } from '../store/signup-data/selectors';
import { Data } from 'signup-constants/signupData/Data';
import { retryEndpoint } from '../store/http-responses/action-creators';
import { EndpointKey } from '../http/EndpointKey';
import { getEndpointRetries } from '../store/http-responses/selectors';
import { getNextSteps } from '../store/flow/selectors';
import { updateStepOrdersInDomainStep } from '../http-gtkm-company-info/updateStepOrders';
import { updateStepOrdersInDomainStepPurchase } from '../http-gtkm-company-info/updateStepOrderPurchase';
import { isPurchaseProductisedFlow } from '../utils/gtkm/isPurchaseProductisedFlow';
import { setGtkmAnimationStart, setGtkmShowingErrorModal, setGtkmAnimationShowingCount, setGtkmHasDomainChangedInDomainStep } from '../store/gtkm/action-creator';
import { getGtkmAnimationShowingCount } from '../store/gtkm/selector';
import { useView } from '../views/useView';
import { Step } from 'signup-constants/Step';
import { getGTKMEndpointKey } from '../utils/gtkm/getGTKMEndpointKey';
import { trackEvent } from '../tracking/trackEvent';
import { EventName } from '../tracking/EventName';
import { advanceToLoadingPageAfterDomainSU0004, advanceToReviewAfterDomainSU0004 } from '../http-gtkm-company-info/updateStepOrderSU0004';
export const hasGTKMDomainChanged = state => {
  const companyDomain = getSignupDataValue(state, Data.CompanyDomain);
  let gtkmCompanyDomain = getSignupDataGtkmData(state, Data.CompanyDomain);
  if (gtkmCompanyDomain === 'hubspot.com') {
    gtkmCompanyDomain = 'hubspot-test.com';
  }
  return gtkmCompanyDomain !== companyDomain;
};
export const useHasDomainChanged = () => {
  const appState = useSelector(state => state);
  return hasGTKMDomainChanged(appState);
};
export const useGTKMDomainSubmit = () => {
  const dispatch = useDispatch();
  const hasDomainChanged = useHasDomainChanged();
  const gtkmDomain = useSelector(state => getSignupDataGtkmData(state, Data.CompanyDomain));
  const animationShowingCount = useSelector(state => getGtkmAnimationShowingCount(state));
  const {
    submit
  } = useView({
    stepKey: Step.CompanyDomain
  });
  const appState = useSelector(state => state);
  const endpointKey = getGTKMEndpointKey(appState);
  const gtkmEndpointRetries = useSelector(state => getEndpointRetries(state, endpointKey));
  const haveNotInvokedSecondGTKMAPI = gtkmEndpointRetries < 1;
  const handleSubmit = () => {
    dispatch(setGtkmShowingErrorModal(false));
    dispatch(setGtkmHasDomainChangedInDomainStep(hasDomainChanged));
    // In the following cases, we don't want to show the animation and hope just to go to next step directly
    // 1. The second GTKM API have been invoked
    // 2. The domain hasn't changed but already shown the animation once
    if (!haveNotInvokedSecondGTKMAPI || !hasDomainChanged && animationShowingCount >= 1) {
      submit();

      // When the domain has changed and we haven't invoked the second GTKM API,
      // we want to show the animation and retry the second GTKM API
    } else if (hasDomainChanged && haveNotInvokedSecondGTKMAPI) {
      dispatch(setGtkmAnimationStart());
      dispatch(setGtkmAnimationShowingCount(animationShowingCount + 1));
      dispatch(retryEndpoint(endpointKey));

      // When the domain has not been changed and we haven't invoked the second GTKM API,
      // we want to show the animation and update the step orders
    } else {
      dispatch(setGtkmAnimationStart());
      dispatch(setGtkmAnimationShowingCount(animationShowingCount + 1));
      if (isPurchaseProductisedFlow()) {
        updateStepOrdersInDomainStepPurchase({
          gtkmData: {
            [Data.CompanyDomain]: gtkmDomain
          },
          dispatch,
          state: appState
        });
      } else {
        updateStepOrdersInDomainStep({
          gtkmData: {
            [Data.CompanyDomain]: gtkmDomain
          },
          dispatch,
          state: appState
        });
      }
    }
  };
  const handleSubmitSU0004 = () => {
    dispatch(setGtkmHasDomainChangedInDomainStep(hasDomainChanged));
    const hasInvokedSecondGTKMAPI = gtkmEndpointRetries >= 1;
    if (hasInvokedSecondGTKMAPI) {
      submit();
      return;
    }
    if (hasDomainChanged) {
      // Show LoadingPage while the second GTKM call is in flight so the user
      // isn't left staring at the CompanyDomain form. The response handler
      // advances from LoadingPage to Review when the call resolves.
      advanceToLoadingPageAfterDomainSU0004({
        gtkmData: {
          [Data.CompanyDomain]: gtkmDomain
        },
        dispatch,
        state: appState
      });
      dispatch(retryEndpoint(EndpointKey.GTKMCompanyInfoSU0004));
      const nextSteps = getNextSteps(appState);
      trackEvent(EventName.StepCompleted, Object.assign({
        stepName: Step.CompanyDomain
      }, nextSteps.length > 0 && {
        nextStepName: nextSteps[0]
      }, {
        isSkipped: getSignupDataSkipped(appState, Data.CompanyDomain)
      }));
      return;
    }
    advanceToReviewAfterDomainSU0004({
      gtkmData: {
        [Data.CompanyDomain]: gtkmDomain
      },
      dispatch,
      state: appState
    });
    const nextSteps = getNextSteps(appState);
    trackEvent(EventName.StepCompleted, Object.assign({
      stepName: Step.CompanyDomain
    }, nextSteps.length > 0 && {
      nextStepName: nextSteps[0]
    }, {
      isSkipped: getSignupDataSkipped(appState, Data.CompanyDomain)
    }));
  };
  return {
    handleSubmit,
    handleSubmitSU0004
  };
};